import { FlagBagStatus } from "services/api/flagbags/types";

export interface ConflictItem {
    flagBagId: string;
    localStatus: string;
    serverStatus: string;
    localTimestamp?: number;
    serverTimestamp?: number;
    reason?: string;
    serverUpdatedBy?: string;
}

/**
 * 检测旗袋状态冲突
 * @param localData 本地数据
 * @param serverData 服务器数据
 * @returns 冲突的旗袋列表
 */
export const detectFlagBagConflicts = (
    localData: FlagBagStatus[],
    serverData: FlagBagStatus[]
): ConflictItem[] => {
    const conflicts: ConflictItem[] = [];
    const serverMap = new Map(serverData.map(item => [item.flagBagId, item]));

    localData.forEach(localItem => {
        const serverItem = serverMap.get(localItem.flagBagId);
        
        if (!serverItem) {
            // 服务器上不存在该旗袋，可能是新创建的或已删除
            return;
        }

        // 检查状态是否一致
        if (localItem.currentStatus !== serverItem.currentStatus) {
            conflicts.push({
                flagBagId: localItem.flagBagId,
                localStatus: localItem.currentStatus.toString(),
                serverStatus: serverItem.currentStatus.toString(),
                // FlagBagStatus 不包含时间戳信息，保持为 undefined
                localTimestamp: undefined,
                serverTimestamp: undefined,
            });
        }
    });

    return conflicts;
};

/**
 * 存储冲突信息到 localStorage
 * @param conflicts 冲突列表
 */
export const saveConflictsToStorage = (conflicts: ConflictItem[]): void => {
    try {
        localStorage.setItem("dataConflicts", JSON.stringify(conflicts));
        localStorage.setItem("dataConflictsTimestamp", new Date().toISOString());
    } catch (error) {
        console.error("Failed to save conflicts to storage:", error);
    }
};

/**
 * 从 localStorage 读取冲突信息
 * @returns 冲突列表
 */
export const loadConflictsFromStorage = (): ConflictItem[] | null => {
    try {
        const conflictsStr = localStorage.getItem("dataConflicts");
        if (!conflictsStr) {
            return null;
        }
        return JSON.parse(conflictsStr);
    } catch (error) {
        console.error("Failed to load conflicts from storage:", error);
        return null;
    }
};

/**
 * 清除存储的冲突信息
 */
export const clearConflictsFromStorage = (): void => {
    localStorage.removeItem("dataConflicts");
    localStorage.removeItem("dataConflictsTimestamp");
};

/**
 * 检查是否有未解决的冲突
 * @returns 冲突数量
 */
export const hasUnresolvedConflicts = (): number => {
    const conflicts = loadConflictsFromStorage();
    return conflicts ? conflicts.length : 0;
};

